'use client';

import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';

import ModalDiscardForm from 'shared/component/CustomModal';
import useCustomModal from 'store/provider/ModalProvider';

interface UnsavedChangesContextType {
	handleDiscardModal: (action: () => void) => void;
	isDirty: boolean;
	setIsDirty: (value: boolean) => void;
	forceRedirect: (url: string, target: '_self' | '_blank') => void;
}

interface UnsavedChangesProviderProps {
	children?: ReactNode;
}

interface UseUnsavedChangesProps {
	isDirty?: boolean;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | undefined>(undefined);

/**
 * Custom hook to manage unsaved changes state.
 * Ensures components within `UnsavedChangesProvider` can track and prevent unintended navigation.
 *
 * @param {UseUnsavedChangesProps} isDirty - Indicates whether there are unsaved changes.
 * @returns {UnsavedChangesContextType} - Context value with state management functions.
 * @throws {Error} - Throws an error if used outside `UnsavedChangesProvider`.
 * @example
 * const { isDirty, setIsDirty, handleDiscardModal } = useUnsavedChanges({ isDirty: true });
 * const onCancel = () => {
 *  handleDiscardModal(() => {
 *    router.push('/assessment');
 *  });
 * }
 * <MyComponent onCancel={onCancel}>
 *  <Button onClick={onCancel}>Cancel</Button>
 * </MyComponent>
 */
export const useUnsavedChanges = (props?: UseUnsavedChangesProps): UnsavedChangesContextType => {
	const context = useContext(UnsavedChangesContext);
	if (!context) {
		throw new Error('useUnsavedChanges must be used within UnsavedChangesProvider');
	}

	useEffect(() => {
		const setIsDirty = context.setIsDirty;

		if (props?.isDirty) {
			setIsDirty(true);
			return;
		}

		setIsDirty(false);
	}, [context.setIsDirty, props?.isDirty]);

	return context;
};

/**
 * Provides a context for managing unsaved changes, displaying a discard confirmation modal,
 * and preventing unintended navigation when changes are unsaved.
 *
 * @param {UnsavedChangesProviderProps} children - React child components.
 * @returns {JSX.Element} - The provider component wrapping child elements.
 * @example
 * <UnsavedChangesProvider>
 *   <MyComponent />
 * </UnsavedChangesProvider>
 */
export const UnsavedChangesProvider: React.FC<UnsavedChangesProviderProps> = ({ children }) => {
	const [isDirty, setIsDirty] = useState(false);
	const { handleModal, modals } = useCustomModal();
	const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
	const skipBlockRef = useRef(false);

	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (isDirty) {
				// Only show browser prompt for page refresh/close
				e.preventDefault();
				e.returnValue = '';
			}
		};

		// Intercept navigation attempts
		const handleClick = (e: MouseEvent) => {
			const target = e.target as HTMLElement;
			const anchor = target.closest('a');
			if (anchor && anchor.href && !anchor.href.startsWith('javascript:')) {
				if (isDirty && !skipBlockRef.current) {
					e.preventDefault();
					setPendingAction(() => () => (window.location.href = anchor.href));
					handleModal('discardModal', 'open');
				}
			}
		};

		// Handle browser back button
		const handlePopState = () => {
			if (isDirty && !skipBlockRef.current) {
				setPendingAction(() => () => window.history.back());
				handleModal('discardModal', 'open');
				window.history.pushState(null, '', window.location.href);
			}
		};

		window.addEventListener('click', handleClick, true);
		window.addEventListener('beforeunload', handleBeforeUnload);
		window.addEventListener('popstate', handlePopState);

		return () => {
			window.removeEventListener('click', handleClick, true);
			window.removeEventListener('beforeunload', handleBeforeUnload);
			window.removeEventListener('popstate', handlePopState);
		};
	}, [isDirty, handleModal]);

	const handleConfirm = (): void => {
		handleModal('discardModal', 'close');
		setIsDirty(false); // Reset isDirty before navigation
		if (pendingAction) {
			pendingAction();
			setPendingAction(null);
		}
	};

	const forceRedirect = (url: string, target: '_self' | '_blank' = '_self') => {
		skipBlockRef.current = true;
		window.open(url, target);
	};

	const handleCancel = (): void => {
		handleModal('discardModal', 'close');
		setPendingAction(null);
	};

	const handleDiscardModal = (action: () => void): void => {
		if (!isDirty) {
			action();
			return;
		}

		setPendingAction(() => action);
		handleModal('discardModal', 'open');
	};

	const contextValue: UnsavedChangesContextType = {
		handleDiscardModal,
		isDirty,
		setIsDirty,
		forceRedirect,
	};

	return (
		<UnsavedChangesContext.Provider value={contextValue}>
			{children}
			<ModalDiscardForm
				open={modals.discardModal}
				title="Batalkan"
				description="Apakah Anda yakin ingin meninggalkan halaman ini?"
				handleClose={handleCancel}
				handleConfirm={handleConfirm}
				confirmText="Batalkan"
				cancelText="Tetap"
			/>
		</UnsavedChangesContext.Provider>
	);
};
